import { wikiPathLabel } from '../shared/wikiFormat';

/**
 * Pure helpers for the New page modal's floating location picker
 * (C2-modals-import §1.6, prototype `locs` / `wkLocOpts` / `wkToggleLoc` /
 * `wkLocKey`).
 */

/**
 * Prototype `locs`: every category in tree order, each followed by its
 * subcategories — `{ id, label: "Cat" | "Cat › Sub", isSub }` plus what the UI
 * and the create call need.
 *
 * Real-build rule (the prototype had no ACLs): only destinations the caller
 * can write to are offered (`can_edit !== false`; POST /wiki/pages checks the
 * same edit access). A writable subcategory under a read-only category stays
 * pickable; it is shown with its "in {Category}" path because its parent row
 * is not in the list (`parentListed: false`).
 */
export function buildWikiLocations(tree, { writableOnly = true } = {}) {
    const out = [];
    (Array.isArray(tree) ? tree : []).forEach((cat) => {
        if (!cat || cat.archived) return;
        const catId = cat.id || cat.category_id;
        if (!catId) return;
        const catListed = !writableOnly || cat.can_edit !== false;
        if (catListed) {
            out.push({
                id: catId,
                label: cat.name || '',
                name: cat.name || '',
                isSub: false,
                categoryId: catId,
                subcategoryId: null,
                parentName: null,
                parentListed: false,
                icon: cat.icon || 'folder',
                color: cat.color || null,
            });
        }
        (cat.subs || cat.subcategories || cat.children || []).forEach((sub) => {
            if (!sub || sub.archived) return;
            if (writableOnly && sub.can_edit === false) return;
            const subId = sub.id || sub.category_id;
            if (!subId) return;
            out.push({
                id: subId,
                label: wikiPathLabel(cat.name || '', sub.name || ''),
                name: sub.name || '',
                isSub: true,
                categoryId: catId,
                subcategoryId: subId,
                parentName: cat.name || '',
                parentListed: catListed,
                icon: 'folder',
                color: null,
            });
        });
    });
    return out;
}

const norm = (query) => String(query || '').toLowerCase().trim();

/** Case-insensitive substring of the FULL label ("sales" → Sales and every "Sales › …"). */
export function filterWikiLocations(locations, query) {
    const q = norm(query);
    return (locations || []).filter((loc) => !q || loc.label.toLowerCase().indexOf(q) > -1);
}

/** Prototype: the highlight index is clamped into the filtered list (no wrap). */
export function clampHighlight(hi, length) {
    return Math.min(Math.max(0, Number(hi) || 0), Math.max(0, length - 1));
}

/** ↓ / ↑: move by `delta`, clamped to [0, length-1]. */
export function moveHighlight(hi, delta, length) {
    if (length <= 0) return 0;
    return Math.max(0, Math.min(length - 1, clampHighlight(hi, length) + delta));
}

/**
 * The rows the popover renders (prototype `wkLocOpts`): sub rows show the
 * subcategory name only, indented 32px while unfiltered; with a query they
 * are un-indented and carry "in {Category}" on the right.
 */
export function pickerRows(locations, query, currentId, hi) {
    const q = norm(query);
    const list = filterWikiLocations(locations, q);
    const hiIndex = clampHighlight(hi, list.length);
    return list.map((loc, index) => {
        const showPath = loc.isSub && (Boolean(q) || !loc.parentListed);
        return {
            ...loc,
            text: loc.isSub ? loc.name : loc.label,
            indent: loc.isSub && !q && loc.parentListed,
            pathLabel: showPath ? `in ${loc.parentName}` : '',
            on: loc.id === currentId,
            isHi: index === hiIndex,
        };
    });
}

/** First candidate id that is an offered location, else the first location (prototype `locs[0]`). */
export function resolveDefaultLocation(locations, candidates = []) {
    const list = locations || [];
    for (const id of candidates) {
        if (id && list.some((loc) => loc.id === id)) return id;
    }
    return list.length ? list[0].id : null;
}

export const PICKER_FLIP_MIN = 280;
export const PICKER_MIN_HEIGHT = 160;
export const PICKER_MAX_HEIGHT = 400;

/**
 * Prototype `wkToggleLoc` positioning, computed once from the trigger's
 * `getBoundingClientRect()` when the picker opens:
 *   below = vh − rect.bottom − 16, above = rect.top − 16
 *   down  = below ≥ 280 || below ≥ above   (opens upward only when < 280px
 *           below AND there is more room above)
 *   maxH  = clamp(down ? below : above, 160, 400)
 *   left/width match the trigger; top = rect.bottom + 6 (down) or
 *   bottom = vh − rect.top + 6 (up). All rounded.
 */
export function computePickerPosition(rect, viewportHeight) {
    const r = rect || { left: 0, top: 0, bottom: 0, width: 320 };
    const vh = Number(viewportHeight) || 800;
    const below = vh - r.bottom - 16;
    const above = r.top - 16;
    const down = below >= PICKER_FLIP_MIN || below >= above;
    const maxH = Math.max(PICKER_MIN_HEIGHT, Math.min(PICKER_MAX_HEIGHT, down ? below : above));
    return {
        left: Math.round(r.left),
        width: Math.round(r.width),
        top: Math.round(r.bottom + 6),
        bottom: Math.round(vh - r.top + 6),
        down,
        maxH: Math.round(maxH),
    };
}

/** Inline style for the fixed popover from `computePickerPosition`. */
export function pickerPopoverStyle(pos) {
    if (!pos) return { left: 0, width: 320, top: 0, maxHeight: 320 };
    return pos.down
        ? { left: pos.left, width: pos.width, top: pos.top, maxHeight: pos.maxH }
        : { left: pos.left, width: pos.width, bottom: pos.bottom, maxHeight: pos.maxH };
}
